import type {
  AtlasAgentMessage,
  AtlasGestureStep,
  AtlasPin,
  AtlasReplayResultMessage,
  Thread,
} from '@atlas/api-contract';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';

import { normaliseScreenPath } from '@/features/viewer/screen-paths';
import { useViewerStore } from '@/features/viewer/viewer-store';

import { PinOverlay } from './pin-overlay';
import { usePinPositionsStore } from './pin-positions-store';
import { ThreadPanel } from './thread-panel';
import { threadScreenMatches } from './thread-display';
import { useThreadsQuery } from './threads-queries';
import { viewportSwitchOffer } from './viewport-offer';
import {
  nextSelectedThreadId,
  selectPanelThreads,
  selectPinThreads,
  shouldShowResolvedPins,
  toAtlasPins,
  toThreadListQuery,
} from './visible-threads';

/**
 * The threads half of the viewer, assembled.
 *
 * ============================================================================
 * WHY THIS IS A HOOK THAT RETURNS TWO NODES
 * ============================================================================
 * The feature has to appear in two places that are nowhere near each other in
 * the tree: the panel is a sibling of the whole canvas, and the overlay is a
 * child of the frame's positioning box, several components down inside it. They
 * are nonetheless one thing — they share the query, the selection, the hover,
 * and the filters that decide what either of them shows — and splitting them
 * into two components would mean either two subscriptions to reconcile or
 * fifteen props threaded through `ViewerShell` twice.
 *
 * So the shell asks for both and puts each where it belongs. Everything the two
 * share stays in here.
 *
 * ============================================================================
 * THE AGENT IS TOLD WHAT TO DRAW; IT IS NEVER ASKED
 * ============================================================================
 * `atlas:render-pins` replaces the agent's entire pin set, and this hook is the
 * only thing that sends it. That is what makes the panel's filters mean
 * something on the canvas: the message carries the threads that survived the
 * filters and nothing else, so hiding resolved threads in the list hides their
 * pins in the prototype at the same instant (spec R6.2).
 *
 * Two facts about the agent shape how that is sent, and both were read out of
 * `packages/agent`:
 *
 *  1. **A navigation empties the agent's pin set.** `NavigationWatcher` clears
 *     `states` and redraws on every `atlas:navigated`, including a bare hash
 *     change that never reloads the document. The pins therefore have to be
 *     re-sent afterwards *even when the visible set is byte-identical* — which
 *     a naive "only send when the pins change" would skip, leaving a screen
 *     with no markers on it. `agentGeneration` exists for exactly that: it
 *     ticks on every `ready`/`navigated` and is part of the key that decides
 *     whether a send is redundant.
 *  2. **The agent reports positions only for pins it is drawing, and says
 *     nothing at all when it is drawing none.** So an empty set is cleared from
 *     the position store here; waiting for a message that will never arrive
 *     would leave the last set's coordinates describing markers that are gone.
 *
 * Beyond that the sends are deduplicated, because `atlas:render-pins` makes the
 * agent re-resolve every selector against the DOM, and doing that on every
 * React render would put a full pass of `querySelector` calls between the
 * reviewer and every keystroke they type into a composer.
 */

/** The subset of the bridge this feature sends on. */
export interface ThreadWorkspaceBridge {
  readonly navigate: (screenPath: string) => void;
  readonly renderPins: (pins: readonly AtlasPin[], showResolved: boolean) => void;
  readonly highlightPin: (threadId: string | null) => void;
  readonly scrollToPin: (threadId: string) => void;
  readonly replay: (
    threadId: string,
    gesturePath: readonly AtlasGestureStep[],
    expectedScreenKey: string | null,
  ) => Promise<AtlasReplayResultMessage>;
}

export interface UseThreadWorkspaceOptions {
  /** `null` until a version resolves, or for a prototype with no versions. */
  readonly versionId: string | null;
  /** The agent has announced itself and will act on what it is sent. */
  readonly agentReady: boolean;
  /** The version's own URL path, stripped from the screen labels in the panel. */
  readonly basePath: string | null;
  /** The version's entry file, for naming the screen served by the bare URL. */
  readonly entryPath: string | null;
  readonly bridge: ThreadWorkspaceBridge;
}

export interface ThreadWorkspace {
  /** The right-hand panel. Sits beside the canvas. */
  readonly panel: ReactNode;
  /** The pin popovers and hit targets. Goes in `PrototypeFrame`'s overlay slot. */
  readonly overlay: ReactNode;
  /** Every agent message, after the shell has taken what it needs. */
  readonly onAgentMessage: (message: AtlasAgentMessage) => void;
}

/** Shared empty list, so "no data yet" is not a new array on every render. */
const EMPTY_THREADS: readonly Thread[] = [];

/** Long enough to read the offer and decide; short enough not to camp. */
const VIEWPORT_OFFER_DURATION_MS = 8_000;

export const useThreadWorkspace = ({
  versionId,
  agentReady,
  basePath,
  entryPath,
  bridge,
}: UseThreadWorkspaceOptions): ThreadWorkspace => {
  const { navigate, renderPins, highlightPin, scrollToPin, replay } = bridge;

  const filters = useViewerStore((state) => state.filters);
  const screenPath = useViewerStore((state) => state.screenPath);
  const screenKey = useViewerStore((state) => state.screenKey);
  const widthPreset = useViewerStore((state) => state.widthPreset);
  const panelCollapsed = useViewerStore((state) => state.panelCollapsed);
  const pendingPin = useViewerStore((state) => state.pendingPin);
  const selectedThreadId = useViewerStore((state) => state.selectedThreadId);
  const highlightedThreadId = useViewerStore(
    (state) => state.highlightedThreadId,
  );
  const offeredThreadIds = useViewerStore(
    (state) => state.viewportOfferedThreadIds,
  );

  const setFilters = useViewerStore((state) => state.setFilters);
  const togglePanel = useViewerStore((state) => state.togglePanel);
  const setPendingPin = useViewerStore((state) => state.setPendingPin);
  const selectThreadId = useViewerStore((state) => state.selectThread);
  const highlightThread = useViewerStore((state) => state.highlightThread);
  const markViewportOffered = useViewerStore(
    (state) => state.markViewportOffered,
  );
  const matchViewportWidth = useViewerStore((state) => state.matchViewportWidth);

  const setPositions = usePinPositionsStore((state) => state.setPositions);
  const clearPositions = usePinPositionsStore((state) => state.clear);

  /* ---------------------------------------------------------------------- */
  /* Data                                                                   */
  /* ---------------------------------------------------------------------- */

  const listQuery = useMemo(() => toThreadListQuery(filters), [filters]);
  const threads = useThreadsQuery(versionId, listQuery);
  const data = threads.data ?? EMPTY_THREADS;

  const selection = useMemo(
    () => ({
      threads: data,
      filters,
      currentScreenPath: screenPath,
      currentScreenKey: screenKey,
    }),
    [data, filters, screenPath, screenKey],
  );

  const panelThreads = useMemo(
    () => selectPanelThreads(selection),
    [selection],
  );
  const pinThreads = useMemo(() => selectPinThreads(selection), [selection]);

  /*
   * Looked up in the *unfiltered* list rather than in `panelThreads`. A thread
   * the filters are hiding has no pin, so its popover cannot be positioned and
   * stays invisible anyway — but resolving a thread while the panel is showing
   * only open ones would otherwise rip the popover away mid-click, before the
   * reviewer had seen the resolve land.
   */
  const selectedThread = useMemo(
    () => data.find((thread) => thread.id === selectedThreadId) ?? null,
    [data, selectedThreadId],
  );

  /* ---------------------------------------------------------------------- */
  /* Selecting a thread (spec R3.6, R6.3)                                   */
  /* ---------------------------------------------------------------------- */

  /*
   * A fresh object per request rather than a bare id, so that clicking the same
   * row twice scrolls to it twice. Requesting the scroll and performing it have
   * to be separate: the pin may be on another screen, and `atlas:scroll-to-pin`
   * answers with an error rather than a scroll if the marker is not drawn yet.
   */
  const [scrollRequest, setScrollRequest] = useState<{
    readonly threadId: string;
  } | null>(null);

  /*
   * The half of opening a thread that is the same however the screen was
   * reached: select it (which the queued-scroll-after-drawn effect below turns
   * into a scroll once the pin lands) and, once per thread, offer to match the
   * width it was authored at.
   */
  const selectAndOffer = useCallback(
    (thread: Thread) => {
      selectThreadId(thread.id);
      setScrollRequest({ threadId: thread.id });

      const offer = viewportSwitchOffer({
        threadId: thread.id,
        threadViewportWidth: thread.viewportWidth,
        currentPreset: widthPreset,
        offeredThreadIds,
      });

      if (offer === null) {
        return;
      }

      /*
       * Recorded before the reviewer answers, and whatever they answer. The
       * offer is a decision about a thread rather than about a moment: asking
       * again on the next visit would make declining impossible to make stick.
       */
      markViewportOffered(thread.id);

      toast(`This comment was left at ${offer.label} width.`, {
        description:
          'Its pin is only exactly where its author put it at that width.',
        duration: VIEWPORT_OFFER_DURATION_MS,
        action: {
          label: `Switch to ${offer.label}`,
          onClick: () => {
            matchViewportWidth(thread.viewportWidth);
          },
        },
      });
    },
    [
      markViewportOffered,
      matchViewportWidth,
      offeredThreadIds,
      selectThreadId,
      widthPreset,
    ],
  );

  const openThread = useCallback(
    (thread: Thread) => {
      /*
       * A pin on a single-URL client-side app lives in a state the prototype has
       * to be *driven* back into — a screen switch, a filled form, an opened row
       * — that no URL can express. When the thread recorded that trace, replay it
       * and wait: the agent runs the steps and answers `atlas:replay-result`, and
       * only then is the pin where its author left it, ready to scroll to. On any
       * failure the screen could not be restored, so we say so and still open the
       * popover/panel row rather than swallowing the click (snapshot fallback is
       * deferred).
       */
      if (thread.gesturePath.length > 0) {
        void replay(thread.id, thread.gesturePath, thread.screenKey).then(
          (result) => {
            if (!result.ok) {
              toast.error(
                "Couldn't restore the screen this comment was placed on.",
              );
            }

            selectAndOffer(thread);
          },
          () => {
            toast.error(
              "Couldn't restore the screen this comment was placed on.",
            );

            selectAndOffer(thread);
          },
        );

        return;
      }

      /*
       * Classic URL-navigated prototype: no trace to replay, so a screen change
       * is a plain navigation. Sent verbatim, which is the only spelling that
       * works. A thread's `screenPath` is what the agent reported when the pin
       * was placed — `location.pathname` plus hash — so it already carries the
       * `/<prototypeId>/<versionId>/` prefix the files server serves under. The
       * agent resolves the target with `new URL(path, location.href)`, so a
       * version-relative `/settings.html` would resolve against the sandbox
       * *origin* and navigate the frame clean out of the prototype.
       */
      if (
        !threadScreenMatches(
          thread.screenPath,
          screenPath,
          thread.screenKey,
          screenKey,
        )
      ) {
        navigate(normaliseScreenPath(thread.screenPath));
      }

      selectAndOffer(thread);
    },
    [navigate, replay, screenKey, screenPath, selectAndOffer],
  );

  const openThreadById = useCallback(
    (threadId: string) => {
      const thread = data.find((candidate) => candidate.id === threadId);

      if (thread !== undefined) {
        openThread(thread);
      }
    },
    [data, openThread],
  );

  const closeThread = useCallback(() => {
    setScrollRequest(null);
    selectThreadId(null);
  }, [selectThreadId]);

  /* ---------------------------------------------------------------------- */
  /* Talking to the agent                                                   */
  /* ---------------------------------------------------------------------- */

  /*
   * Bumped on every `atlas:ready` and `atlas:navigated`. Both leave the agent
   * with an empty pin set, so both have to force a re-send even when the pins
   * themselves have not changed. A ref would not do: resetting one schedules
   * nothing, and the effect below would simply never run again.
   */
  const [agentGeneration, setAgentGeneration] = useState(0);

  const pins = useMemo(() => toAtlasPins(pinThreads), [pinThreads]);
  const showResolved = shouldShowResolvedPins(filters);

  const pinSignature = useMemo(
    () => `${String(showResolved)}|${JSON.stringify(pins)}`,
    [pins, showResolved],
  );

  const sentPinsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!agentReady) {
      /*
       * The channel is down and whatever the last agent knew died with it.
       * Forgetting what was sent is what makes the next `atlas:ready` a full
       * re-statement rather than a no-op.
       */
      sentPinsRef.current = null;
      return;
    }

    const key = `${String(agentGeneration)}|${pinSignature}`;

    if (sentPinsRef.current === key) {
      return;
    }

    sentPinsRef.current = key;
    renderPins(pins, showResolved);

    if (pins.length === 0) {
      clearPositions();
    }
  }, [
    agentGeneration,
    agentReady,
    clearPositions,
    pinSignature,
    pins,
    renderPins,
    showResolved,
  ]);

  /* Spec R6.3, the canvas half: a hovered row emphasises its marker. */
  useEffect(() => {
    if (agentReady) {
      highlightPin(highlightedThreadId);
    }
  }, [agentReady, highlightPin, highlightedThreadId]);

  /*
   * Declared *after* the render-pins effect on purpose. Effects run in order
   * within a commit, so `atlas:render-pins` is posted before
   * `atlas:scroll-to-pin` — and the agent refuses to scroll to a marker it has
   * not drawn. The request is held until the thread is actually in the drawn
   * set, which is what carries a selection across a navigation: the pin appears
   * on the new screen, and only then is the scroll asked for.
   */
  useEffect(() => {
    if (scrollRequest === null || !agentReady) {
      return;
    }

    if (!pinThreads.some((thread) => thread.id === scrollRequest.threadId)) {
      return;
    }

    setScrollRequest(null);
    scrollToPin(scrollRequest.threadId);
  }, [agentReady, pinThreads, scrollRequest, scrollToPin]);

  /*
   * The prototype navigated away from the screen an open popover belongs to, so
   * the popover closes rather than lingering invisibly over a page its thread
   * has nothing to do with.
   *
   * The `scrollRequest` guard is what keeps this from fighting the selection
   * flow: selecting a thread on another screen sets the selection *first* and
   * then asks the agent to navigate, so for the moment in between the thread
   * legitimately does not match the screen on display. The request is only
   * cleared once the pin has actually been drawn, which is exactly the point
   * from which a mismatch means the reviewer has moved on.
   */
  useEffect(() => {
    if (selectedThread === null || screenPath === null) {
      return;
    }

    if (scrollRequest !== null && scrollRequest.threadId === selectedThread.id) {
      return;
    }

    if (
      !threadScreenMatches(
        selectedThread.screenPath,
        screenPath,
        selectedThread.screenKey,
        screenKey,
      )
    ) {
      selectThreadId(null);
    }
  }, [screenKey, screenPath, scrollRequest, selectThreadId, selectedThread]);

  const onAgentMessage = useCallback(
    (message: AtlasAgentMessage) => {
      switch (message.type) {
        case 'atlas:pin-positions':
          setPositions(message.positions);
          break;

        case 'atlas:pin-clicked':
          /*
           * The agent's own markers are clickable inside the prototype, and the
           * parent lays transparent targets over them for hover. Both routes end
           * up here, so a pin opens exactly what a panel row opens.
           */
          openThreadById(message.threadId);
          break;

        case 'atlas:ready':
        case 'atlas:navigated':
          /*
           * Positions are coordinates in a document that has just been replaced
           * or scrolled out from under them, and the agent has emptied its own
           * pin set. Clear, then let the effect above re-state everything.
           */
          clearPositions();
          setAgentGeneration((generation) => generation + 1);
          break;

        default:
          break;
      }
    },
    [clearPositions, openThreadById, setPositions],
  );

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  const refetchThreads = threads.refetch;

  const handleRetry = useCallback(() => {
    void refetchThreads();
  }, [refetchThreads]);

  if (versionId === null) {
    return { panel: null, overlay: null, onAgentMessage };
  }

  return {
    onAgentMessage,

    panel: (
      <ThreadPanel
        threads={panelThreads}
        isPending={threads.isPending}
        isError={threads.isError}
        error={threads.error}
        onRetry={handleRetry}
        filters={filters}
        onFiltersChange={setFilters}
        screenKnown={screenPath !== null}
        collapsed={panelCollapsed}
        onToggleCollapsed={togglePanel}
        selectedThreadId={selectedThreadId}
        highlightedThreadId={highlightedThreadId}
        onSelect={openThread}
        onHover={highlightThread}
        basePath={basePath}
        entryPath={entryPath}
      />
    ),

    overlay: (
      <PinOverlay
        versionId={versionId}
        pinThreads={pinThreads}
        selectedThread={selectedThread}
        pendingPin={pendingPin}
        onSelect={openThreadById}
        onClose={closeThread}
        onHover={highlightThread}
        onCancelPending={() => {
          setPendingPin(null);
        }}
        onCreated={(thread) => {
          /*
           * The create awaited its own invalidation, so the thread is already in
           * the list and its pin is already on its way to the agent. Selecting
           * it opens the popover on the marker the reviewer just made, rather
           * than leaving them looking at a pin they have to click again.
           */
          selectThreadId(thread.id);
        }}
        onCommentDeleted={(result) => {
          selectThreadId(nextSelectedThreadId(selectedThreadId, result));
        }}
      />
    ),
  };
};
